// Browse "Color by": categorical colors for topics and content types.
//
// Hues come from the dataviz reference categorical palette, skipping orange and
// red so they never read as the site accent ("New", primary buttons). Topic
// hues follow the Map's area colors (AI violet, ingest aqua, analytics yellow,
// governance blue). Both sets pass the palette validator (lightness, chroma,
// color-blind and normal-vision separation for adjacent pairs). Yellow, aqua and
// magenta are under 3:1 on white, so color is never the only signal: they're
// used for marks (stripe, dots) beside a visible text label, never for text.
import { useCallback, useState } from "react";
import type { ContentType, Topic } from "../../shared/model.ts";

export type ColorBy = "topic" | "type" | "off";

export const TOPIC_COLOR: Record<Topic, string> = {
  ai_agents: "#6250d6", // violet
  data_engineering: "#1baf7a", // aqua
  analytics_bi: "#eda100", // yellow
  governance: "#2a78d6", // blue
  platform: "#008300", // green
  business_impact: "#e87ba4", // magenta
};

export const TYPE_COLOR: Record<ContentType, string> = {
  release_note: "#2a78d6", // blue
  blog: "#eda100", // yellow
  announcement: "#e87ba4", // magenta
};

const KEY = "nsp.colorBy.v1";
const read = (): ColorBy => {
  try {
    const v = localStorage.getItem(KEY);
    return v === "type" || v === "off" ? v : "topic";
  } catch {
    return "topic";
  }
};

/** Remembered in this browser; defaults to coloring by topic. */
export function useColorBy(): [ColorBy, (v: ColorBy) => void] {
  const [value, setValue] = useState<ColorBy>(read);
  const set = useCallback((v: ColorBy) => {
    setValue(v);
    try {
      localStorage.setItem(KEY, v);
    } catch {
      /* private mode: keep it for this visit only */
    }
  }, []);
  return [value, set];
}

export function storyColor(by: ColorBy, s: { primaryTopic: Topic; contentType: ContentType }): string | null {
  return by === "topic" ? TOPIC_COLOR[s.primaryTopic] : by === "type" ? TYPE_COLOR[s.contentType] : null;
}
